import { Container, Sprite, Texture } from 'pixi.js';
import { AssetLoader } from './AssetLoader';
import { PlayerHitboxes } from './config/HitboxConfig';
import { Platform } from './Platform';

export class Player extends Container {
  private sprite: Sprite;
  private assetLoader: AssetLoader;
  private groundY: number;
  private catType: 'alici' | 'jasper';
  private runFrames: Texture[];
  private jumpTexture: Texture;
  private gameOverTexture: Texture;
  private stunSlipTexture: Texture;
  private currentFrame = 0;
  private animationTimer = 0;
  private animationSpeed = 0.14;

  private isStunned = false;
  private stunTimer = 0;
  private stunDuration = 0.7;
  private isShaking = false;
  private shakeTimer = 0;
  private shakeDuration = 0.7;
  private shakeIntensity = 5;
  private shakeInterval = 0.05;
  private shakeIntervalTimer = 0;

  private isWalkingIn = false;
  private walkInTargetX = 0;
  private walkInSpeed = 400;

  private isJumping = false;
  private isHoldingJump = false;
  private velocityY = 0;
  private gravity = 1800;
  private gravityWhileHolding = 600;
  private jumpInitialPower = -500;
  private jumpBoostTime = 0;
  private maxBoostDuration = 0.25;

  private isOnPlatform = false;
  private currentPlatform: Platform | null = null;

  private isBonkSticking = false;
  private bonkStickTimer = 0;
  private bonkStickDuration = 0.3;
  private bonkStickY = 0;
  private postBonkCooldown = 0;
  private postBonkCooldownDuration = 0.2;

  constructor(gameWidth: number, gameHeight: number, catType: 'alici' | 'jasper' = 'alici') {
    super();

    this.assetLoader = AssetLoader.getInstance();
    this.groundY = gameHeight * 0.92;
    this.catType = catType;

    this.runFrames = [];
    this.jumpTexture = this.assetLoader.getTexture('alici_jump');
    this.gameOverTexture = this.assetLoader.getTexture('alici_game_over');
    this.stunSlipTexture = this.assetLoader.getTexture('alici_slip');

    this.loadCatTextures(catType);

    this.sprite = new Sprite(this.runFrames[0]);

    this.sprite.anchor.set(0.5, 1);
    this.sprite.scale.set(0.5);

    this.addChild(this.sprite);

    this.x = gameWidth * 0.2;
    this.y = this.groundY;
  }

  private loadCatTextures(catType: 'alici' | 'jasper'): void {
    this.runFrames = [
      this.assetLoader.getTexture(`${catType}_run_1`),
      this.assetLoader.getTexture(`${catType}_run_2`),
      this.assetLoader.getTexture(`${catType}_run_3`),
    ];

    this.jumpTexture = this.assetLoader.getTexture(`${catType}_jump`);
    this.gameOverTexture = this.assetLoader.getTexture(`${catType}_game_over`);
    this.stunSlipTexture = this.assetLoader.getTexture(`${catType}_slip`);
  }

  update(deltaTime: number): void {
    if (this.isWalkingIn) {
      this.x += this.walkInSpeed * deltaTime;

      if (this.x >= this.walkInTargetX) {
        this.x = this.walkInTargetX;
        this.isWalkingIn = false;
      }

      this.animationTimer += deltaTime;
      if (this.animationTimer >= this.animationSpeed) {
        this.animationTimer = 0;
        this.currentFrame = (this.currentFrame + 1) % this.runFrames.length;
        this.sprite.texture = this.runFrames[this.currentFrame];
      }

      return;
    }

    if (this.isShaking) {
      this.shakeTimer -= deltaTime;
      this.shakeIntervalTimer -= deltaTime;

      if (this.shakeTimer <= 0) {
        this.isShaking = false;
        this.shakeTimer = 0;
        this.shakeIntervalTimer = 0;
        this.sprite.x = 0;
        this.sprite.y = 0;
      } else if (this.shakeIntervalTimer <= 0) {
        this.shakeIntervalTimer = this.shakeInterval;
        const shakeX = (Math.random() - 0.5) * this.shakeIntensity;
        const shakeY = (Math.random() - 0.5) * this.shakeIntensity;
        this.sprite.x = shakeX;
        this.sprite.y = shakeY;
      }
    }

    if (this.isStunned) {
      this.stunTimer -= deltaTime;
      if (this.stunTimer <= 0) {
        this.isStunned = false;
        this.stunTimer = 0;
        if (!this.isJumping) {
          this.sprite.texture = this.runFrames[0];
        }
      }
      return;
    }

    if (this.isJumping) {
      // Update post-bonk cooldown
      if (this.postBonkCooldown > 0) {
        this.postBonkCooldown -= deltaTime;
        if (this.postBonkCooldown < 0) {
          this.postBonkCooldown = 0;
        }
      }

      // Handle bonk sticking
      if (this.isBonkSticking) {
        this.bonkStickTimer += deltaTime;

        if (this.bonkStickTimer >= this.bonkStickDuration) {
          // Stop sticking, start falling
          this.isBonkSticking = false;
          this.bonkStickTimer = 0;
          this.postBonkCooldown = this.postBonkCooldownDuration;
          this.velocityY = 0; // Start falling from rest
        } else {
          // Stay stuck to platform bottom
          this.y = this.bonkStickY;
          this.velocityY = 0;
          return; // Don't process normal jump physics
        }
      }

      const currentGravity =
        this.isHoldingJump && this.jumpBoostTime < this.maxBoostDuration ? this.gravityWhileHolding : this.gravity;

      if (this.isHoldingJump && this.jumpBoostTime < this.maxBoostDuration) {
        this.jumpBoostTime += deltaTime;
      }

      this.velocityY += currentGravity * deltaTime;
      this.y += this.velocityY * deltaTime;

      // Ground landing (will be checked after platform landing in game loop)
      if (this.y >= this.groundY) {
        this.y = this.groundY;
        this.velocityY = 0;
        this.isJumping = false;
        this.isHoldingJump = false;
        this.jumpBoostTime = 0;
        this.isOnPlatform = false;
        this.currentPlatform = null;
        this.sprite.texture = this.runFrames[0];
      }
    } else if (this.isOnPlatform) {
      // Check if still on platform (edge detection)
      if (this.currentPlatform && !this.currentPlatform.isPlayerOnPlatform(this.x)) {
        this.startFalling();
      } else {
        // Walking animation on platform
        this.animationTimer += deltaTime;
        if (this.animationTimer >= this.animationSpeed) {
          this.animationTimer = 0;
          this.currentFrame = (this.currentFrame + 1) % this.runFrames.length;
          this.sprite.texture = this.runFrames[this.currentFrame];
        }
      }
    } else {
      // Normal ground running animation
      this.animationTimer += deltaTime;

      if (this.animationTimer >= this.animationSpeed) {
        this.animationTimer = 0;
        this.currentFrame = (this.currentFrame + 1) % this.runFrames.length;
        this.sprite.texture = this.runFrames[this.currentFrame];
      }
    }
  }

  startJump(): void {
    if (this.isStunned || this.isWalkingIn) {
      return;
    }

    // Can jump from ground or platform
    if (!this.isJumping) {
      this.isJumping = true;
      this.isHoldingJump = true;
      this.jumpBoostTime = 0;
      this.velocityY = this.jumpInitialPower;
      this.isOnPlatform = false;
      this.currentPlatform = null;
      this.sprite.texture = this.jumpTexture;
    }
  }

  stopJump(): void {
    this.isHoldingJump = false;
  }

  getIsJumping(): boolean {
    return this.isJumping;
  }

  updatePosition(gameWidth: number, gameHeight: number): void {
    this.groundY = gameHeight * 0.92;
    this.x = gameWidth * 0.2;
    this.y = this.groundY;
  }

  getGroundY(): number {
    return this.groundY;
  }

  getHitbox(): { x: number; y: number; width: number; height: number } {
    const config = this.isJumping ? PlayerHitboxes.jumping : PlayerHitboxes.running;

    const hitboxWidth = this.sprite.width * config.width;
    const hitboxHeight = this.sprite.height * config.height;
    const hitboxOffsetX = this.sprite.width * config.offsetX;
    const hitboxOffsetY = this.sprite.height * config.offsetY;

    const hitboxX = this.x - hitboxWidth / 2 + hitboxOffsetX;
    const hitboxY = this.y - hitboxHeight - hitboxOffsetY;

    return {
      x: hitboxX,
      y: hitboxY,
      width: hitboxWidth,
      height: hitboxHeight,
    };
  }

  setGameOver(): void {
    this.sprite.texture = this.gameOverTexture;
    this.isJumping = false;
    this.isHoldingJump = false;
    this.velocityY = 0;
    this.isStunned = false;
    this.stunTimer = 0;
    this.isShaking = false;
    this.shakeTimer = 0;
    this.shakeIntervalTimer = 0;
    this.sprite.x = 0;
    this.sprite.y = 0;
    this.isWalkingIn = false;
    this.isOnPlatform = false;
    this.currentPlatform = null;
    this.isBonkSticking = false;
    this.bonkStickTimer = 0;
    this.postBonkCooldown = 0;
  }

  triggerStun(): void {
    this.isStunned = true;
    this.stunTimer = this.stunDuration;
    this.sprite.texture = this.stunSlipTexture;
    this.isShaking = true;
    this.shakeTimer = this.shakeDuration;
    this.shakeIntervalTimer = 0;
  }

  getIsStunned(): boolean {
    return this.isStunned;
  }

  startWalkIn(gameWidth: number): void {
    this.walkInTargetX = gameWidth * 0.2;
    this.x = -this.sprite.width;
    this.isWalkingIn = true;
    this.sprite.texture = this.runFrames[0];
    this.currentFrame = 0;
    this.animationTimer = 0;
  }

  isWalkingInComplete(): boolean {
    return !this.isWalkingIn;
  }

  setCat(catType: 'alici' | 'jasper'): void {
    this.catType = catType;
    this.loadCatTextures(catType);
    this.sprite.texture = this.runFrames[0];
    this.currentFrame = 0;
    this.isJumping = false;
    this.isHoldingJump = false;
    this.velocityY = 0;
    this.isStunned = false;
    this.stunTimer = 0;
    this.isShaking = false;
    this.shakeTimer = 0;
    this.shakeIntervalTimer = 0;
    this.sprite.x = 0;
    this.sprite.y = 0;
    this.isWalkingIn = false;
    this.isOnPlatform = false;
    this.currentPlatform = null;
  }

  getCatType(): 'alici' | 'jasper' {
    return this.catType;
  }

  landOnPlatform(platform: Platform, platformY: number): void {
    this.y = platformY;
    this.velocityY = 0;
    this.isJumping = false;
    this.isHoldingJump = false;
    this.jumpBoostTime = 0;
    this.isOnPlatform = true;
    this.currentPlatform = platform;
    this.sprite.texture = this.runFrames[0];
  }

  bonkPlatform(): void {
    // Activate bonk sticking - player stays attached to platform bottom briefly
    if (this.velocityY < 0) {
      this.isBonkSticking = true;
      this.bonkStickTimer = 0;
      // Keep player at their current position (where they bonked)
      this.bonkStickY = this.y;
      this.velocityY = 0;
    }
  }

  private startFalling(): void {
    // Start falling when walking off platform edge
    this.isJumping = true;
    this.velocityY = 0;
    this.isOnPlatform = false;
    this.currentPlatform = null;
    this.isBonkSticking = false;
    this.bonkStickTimer = 0;
    this.postBonkCooldown = 0;
    this.sprite.texture = this.jumpTexture;
  }

  getIsOnPlatform(): boolean {
    return this.isOnPlatform;
  }

  getVelocityY(): number {
    return this.velocityY;
  }

  getIsBonkSticking(): boolean {
    return this.isBonkSticking;
  }

  hasPostBonkCooldown(): boolean {
    return this.postBonkCooldown > 0;
  }
}
